import Core                       from "@/core/Core";
import CompanyTaxSystem           from "@/domains/CompanyTaxSystems/CompanyTaxSystem";
import Company                    from "@/domains/Companies/Company";
import CompaniesDAO               from "@/domains/Companies/CompaniesDAO";
import EdiOperatorsDAO            from "@/domains/CompanyTaxSystems/CompanyTaxSystemsDAO";
import CompanyAdditionalAttribute from "@/domains/Companies/CompanyAdditionalAttribute";
import Partner                    from "@/domains/Partners/Partner";
import Dictionary                 from "@/core/types/Dictionary";
import BankAccountsService        from "@/domains/BankAccounts/BankAccountsService";
import ContextMenuItem            from "@/lib/components/VContextMenu/ContextMenuItem";
import VatRate                    from "@/core/types/VatRate";

/**
 * Сервис для работы с компаниями
 */

class CompaniesService {

  /**
   * Кеш систем налогообложения. Наполняется из Dictionaries общим эндпоинтом.
   * reactive — чтобы потребители перерисовывались, когда словарь долетит после логина.
   */
  static companyTaxSystems = reactive(new Dictionary<number, CompanyTaxSystem>());

  /**
   * Ставки НДС, доступные для Узбекистана.
   *
   * Значения берутся из справочника — он единственный источник. Набор задан
   * списком процентов здесь: справочник плоский, признака страны/режима
   * у ставки нет. Появится признак на беке — фильтр уедет туда.
   */
  static get uzVatRates(): VatRate[] {
    return Core.dictionaries.vatRates.toArray().filter(rate => rate.id === 12);
  }

  static countries = [
    {
      'code':       'CN',
      'name':       'Китай',
      'nameFull':   'Китайская Народная Республика',
      'codeAlpha3': 'CHN',
      'codeNum':    156
    },
    {
      'code':       'ES',
      'name':       'Испания',
      'nameFull':   'Королевство Испания',
      'codeAlpha3': 'ESP',
      'codeNum':    724
    },
    {
      'code':       'BY',
      'name':       'Белоруссия',
      'nameFull':   'Республика Беларусь',
      'codeAlpha3': 'BLR',
      'codeNum':    112
    },
    {
      'code':       'IN',
      'name':       'Индия',
      'nameFull':   'Республика Индия',
      'codeAlpha3': 'IND',
      'codeNum':    356
    },
    {
      'code':       'KZ',
      'name':       'Казахстан',
      'nameFull':   'Республика Казахстан',
      'codeAlpha3': 'KAZ',
      'codeNum':    398
    },
    {
      'code':       'PL',
      'name':       'Польша',
      'nameFull':   'Республика Польша',
      'codeAlpha3': 'POL',
      'codeNum':    616
    },
    {
      'code':       'UZ',
      'name':       'Узбекистан',
      'nameFull':   'Республика Узбекистан',
      'codeAlpha3': 'UZB',
      'codeNum':    860
    },
    {
      'code':       'RU',
      'name':       'Россия',
      'nameFull':   'Российская Федерация',
      'codeAlpha3': 'RUS',
      'codeNum':    643
    },
    {
      'code':       'GB',
      'name':       'Великобритания',
      'nameFull':   'Соединённое Королевство Великобритании и Северной Ирландии',
      'codeAlpha3': 'GBR',
      'codeNum':    826
    },
    {
      'code':       'DE',
      'name':       'Германия',
      'nameFull':   'Федеративная Республика Германия',
      'codeAlpha3': 'DEU',
      'codeNum':    276
    },
    {
      'code':       'CH',
      'name':       'Швейцария',
      'nameFull':   'Швейцарская Конфедерация',
      'codeAlpha3': 'CHE',
      'codeNum':    756
    },
    {
      "code": "SM",
      "name": "Сан-Марино",
      "nameFull": "Сан-Марино",
      "codeAlpha3": null,
      "codeNum": null
    }
  ];

  static async get(id: number): Promise<Company | null> {
    const response = await CompaniesDAO.get(id);

    if (!response.ok) {
      return null;
    }

    return Core.utils.isObject(response.data) ? Company.fromServer(response.data) : null;
  }

  static async getList(
    params?: Parameters<typeof CompaniesDAO.getList>[0]
  ): Promise<Company[]> {
    const response = await CompaniesDAO.getList(params);

    return Core.utils.hydrateArray(response.data, Company.fromServer)
  }

  static async getTaxSystemsList(): Promise<CompanyTaxSystem[]> {
    const data = await EdiOperatorsDAO.getList();
    return Core.utils.isNotEmptyArray(data) ? data.map((item: any) => CompanyTaxSystem.fromServer(item)) as CompanyTaxSystem[] : [];
  }

  static async getAdditionalAttributes(countryCode: string): Promise<Record<string, CompanyAdditionalAttribute>> {
    const response = await Core.rpc.makeRequest('companyAttributeTypes.getList', {
      country: { code: countryCode }
    });

    if (response.ok && Core.utils.isNotEmptyArray(response.data)) {
      return Object.fromEntries(
        response.data.map((item: any) => [
          item.alias,
          CompanyAdditionalAttribute.fromServer(item)
        ])
      );
    }

    Core.showNotification.error('Не получилось загрузить список реквизитов для этой страны');
    return {};
  }

  /** ------- CRUD ------- */

  /** Создание компании. Возвращает созданную компанию с бэка, null — не создана. */
  static async add(company: Company, partnerId?: number): Promise<Company | null> {
    const errors = company.validate();

    if (errors.length > 0) {
      Core.showNotification.error(errors.join('\n'));
      return null;
    }
    else {
      const response = await CompaniesDAO.add(company, partnerId);

      if (response.ok) {
        Core.showNotification.success('Компания успешно добавлена');

        return Core.utils.hydrate(response.data, Company.fromServer);
      }
      else {
        return null;
      }
    }
  }

  static async update(company: Company): Promise<boolean> {
    const errors = company.validate();

    if (errors.length > 0) {
      Core.showNotification.error(errors.join('\n'));
      return false;
    }
    else {
      const response = await CompaniesDAO.update(company);

      if (response.ok) {
        Core.showNotification.success('Компания успешно обновлена');
      }

      return response.data;
    }
  }

  static async delete(id: number): Promise<boolean> {
    const response = await CompaniesDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Компания успешно удалена');
    }

    return response.data;
  }

  /**
   * Удаление с подтверждением. Используется из контекстного меню компании.
   * Старый delete(id) оставлен для прямых вызовов без UX.
   */
  static async deleteWithConfirmation(company: Company): Promise<boolean> {
    const confirmed = await Core.confirm(
      'Удалить эту компанию?',
      `Компания: ${company.name}`,
    );

    if (confirmed === false) {
      return false;
    }
    else {
      return await this.delete(company.id);
    }
  }

  /** ------- открытие диалога создания/редактирования компании ------- */

  /** company === null — создание новой компании партнёра, её соберёт сама карточка */
  static showCompanyCard(
    company: Company | null,
    partner: Partner,
    readOnly: boolean = false,
  ): Promise<boolean> {
    return Core.dialogs.CompanyCard.showAsPromise({ company, partner, readOnly });
  }

  /** Поиск юр лиц продавца. */
  static searchSellerCompanies(pattern: string): Promise<Company[]> {
    return CompaniesService.getList({ partnerId: undefined, pattern, isSeller: true });   // isSeller=true → фильтр по is_seller
  }

  /**
   * Поиск юр. лиц покупателей по названию.
   * Флаг isBuyer передаём явно: раньше бэк (companies.getList) фильтровал по роли
   * только при isSeller === true, а isSeller: false означал «без фильтра по роли»,
   * и в поиск попадали юр. лица всех партнёров, включая продавцов. Бэк доработан:
   * при isBuyer === true выдача ограничена юр. лицами покупателей.
   */
  static searchBuyerCompanies(pattern: string): Promise<Company[]> {
    return CompaniesService.getList({ partnerId: undefined, pattern, isBuyer: true});
  }

  /**
   * Контекстное меню компании. Фабрика, а не геттер: «Открыть» и «Добавить р/с»
   * завязаны на партнёра (его нет в самой строке-компании), а доступность
   * пунктов-действий — на readOnly-режим.
   *
   * @param partner     полный партнёр (нужен карточкам компании и счёта)
   *
   * @param readOnly    режим только-чтение: пункты-действия дизейблятся.
   *
   * @param includeOpen включать ли пункт «Открыть». В гриде секции — true
   *                    (двойной клик не единственный путь). Внутри уже открытой
   *                    карточки — false (иначе пункт откроет второй диалог поверх).
   */
  static getContextMenu(
    partner: Partner,
    readOnly: boolean = false,
    includeOpen: boolean = true,
  ): ContextMenuItem[] {
    const items: ContextMenuItem[] = [];

    if (includeOpen === true) {
      items.push(
        new ContextMenuItem(
          'Открыть',
          'open_in_new',
          (company: Company) => { return CompaniesService.showCompanyCard(company, partner, readOnly); },
          null,
          false,
          true,
        ),
      );
    }

    items.push(
      new ContextMenuItem(
        'Добавить р/с',
        'post_add',
        (company: Company) => { return BankAccountsService.showBankAccountCard(null, partner, company); },
        (company: Company) => {
          const countryCode = company.country?.code ?? null;

          if (readOnly === true) {
            return 'Недоступно в режиме просмотра';
          }

          /** временная мера, чтобы не плодить ошибки */

          else if (countryCode !== 'RU' && countryCode !== 'UZ') {
            return 'В разработке. Добавление р/с на данный момент доступно для юр. лиц РФ и Узбекистана';
          }
          else {
            return null;
          }
        },
      ),
    );

    items.push(
      new ContextMenuItem(
        'История',
        'history',
        (company: Company) => {
          Core.dialogs.FeedDialog.show({
            entityTypeName: 'company',
            entityId:       company.id,
            showAddComment: false,
          });
        },
      ),
    );

    items.push(
      new ContextMenuItem(
        'Удалить',
        'delete',
        (company: Company) => { return CompaniesService.deleteWithConfirmation(company); },
        (_company: Company) => {
          if (readOnly === true) {
            return 'Недоступно в режиме просмотра';
          }
          else {
            return null;
          }
        },
      ),
    );

    return items;
  }
}

export default CompaniesService;
